import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Edit,
  Save,
  Target,
  X,
  Paperclip,
  Plus,
  Trash2,
  Copy,
  MessageSquare,
  ExternalLink,
  FileText,
  EyeIcon,
  EyeOffIcon,
} from "lucide-react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { toast } from "react-hot-toast";
import * as constantValues from "../../constants/constants";
import { clientService } from "../../services/clientService";
import { leadService } from "../../services/leadService";
import * as helperService from "../../services/helperService";
import DeletableAsyncOption from "../deletableAsyncOption";

const createEmptyQuickBooks = () => ({ type: "", username: "", password: "" });
const createEmptyCredential = () => ({
  id: null,
  entity_name: "",
  entity_link: "",
  username: "",
  password: "",
});

const toText = (value) => (value === null || value === undefined ? "" : value);

const mapLeadToClientPrefill = (lead = {}) => {
  const isBusinessLead = lead.type
    ? lead.type === "business"
    : Boolean(lead.company_name || lead.ein);
  return {
    formData: {
      name: toText(lead.name),
      email: toText(lead.email),
      secondary_email: toText(lead.secondary_email),
      phone: toText(lead.phone),
      additional_phones: toText(lead.additional_phones),
      address: toText(lead.address),
      city: toText(lead.city),
      international: toText(lead.international),
      sales_rep_id: lead.sales_rep_id ?? "",
      sales_rep_name: toText(lead.sales_rep_name),
      language: toText(lead.language),
      contact_name: isBusinessLead ? toText(lead.company_name) : "",
      ein: toText(lead.ein),
      industry: toText(lead.industry),
    },
  };
};

const LeadDetail = ({ user, userPermission, onLeadUpdated, onLeadDeleted }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("data");
  const [salesRepDefaults, setSalesRepDefaults] = useState([]);
  const [referPartnerDefaults, setReferPartnerDefaults] = useState([]);
  const [serviceDefaults, setServiceDefaults] = useState([]);
  const [deletingSalesRepId, setDeletingSalesRepId] = useState(null);
  const [deletingReferPartnerId, setDeletingReferPartnerId] = useState(null);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingContent, setEditingContent] = useState("");
  const [newNoteFile, setNewNoteFile] = useState(null);
  const rawNotes = lead?.notes;
  const leadIdentifier = lead?.id || id;
  const noteFileInputRef = useRef(null);
  const [previews, setPreviews] = useState({});
  const [selectedMember] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isQuickbooksEditing, setIsQuickbooksEditing] = useState(false);
  const [quickbooksDraft, setQuickbooksDraft] = useState(
    createEmptyQuickBooks(),
  );
  const [quickbooks, setQuickbooks] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [editingCredentialId, setEditingCredentialId] = useState(null);
  const [credentialDraft, setCredentialDraft] = useState(
    createEmptyCredential(),
  );
  const [visibleCredentialPasswords, setVisibleCredentialPasswords] = useState(
    new Set(),
  );
  const currentUser = useMemo(
    () => selectedMember || user?.name || "",
    [selectedMember, user?.name],
  );
  const leadStatus = leadData?.status;
  const isWaitingDocumentsLead = leadStatus === "Waiting for Documents";
  const isReadyLead = leadStatus === "Ready for Quote";
  const isWaitingAcceptanceLead = leadStatus === "Waiting on Acceptance";
  const isClosedLead = leadStatus === "Closed";
  const isLostLead = leadStatus === "Lost";
  const isPersonalLead = editData?.type
    ? editData.type === "personal"
    : !(editData?.company_name || editData?.ein);

  const loadLead = useCallback(async () => {
    try {
      setLoading(true);
      const [fetched, qb, creds] = await Promise.all([
        leadService.getLead(id),
        leadService.getQuickBooks(id).catch(() => null),
        leadService.getCredentials(id).catch(() => []),
      ]);
      setLead(fetched);
      setLeadData(fetched);
      setEditData(fetched);
      setQuickbooks(qb);
      setCredentials(Array.isArray(creds) ? creds : []);
    } catch (error) {
      toast.error("Failed to load lead");
      console.error("Load lead error:", error);
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  useEffect(() => {
    setNotes(helperService.normalizeNotes(rawNotes));
  }, [rawNotes]);

  const loadPreview = async (note) => {
    try {
      if (!note.asset_url) return;
      const { blob, contentType } = await leadService.getNoteFile(
        leadIdentifier,
        note.asset_url,
      );

      // For textual types, we can also extract text
      let text = null;
      if (contentType.startsWith("text/")) {
        text = await blob.text();
      }

      const url = URL.createObjectURL(blob);
      setPreviews((p) => {
        // cleanup previous url for the same note (avoid leaks)
        if (p[note.id]?.url) URL.revokeObjectURL(p[note.id].url);
        return { ...p, [note.id]: { url, type: contentType, text } };
      });
    } catch (e) {
      toast.error("Failed to load preview");
      console.error(e);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(previews).forEach(
        (pv) => pv?.url && URL.revokeObjectURL(pv.url),
      );
    };
  }, [previews]);

  const handleTabChange = async (nextTabId) => {
    if (activeTab === "data" && nextTabId !== "data" && infoDirty) {
      const saveNow = window.confirm(
        "You have unsaved info. Save before leaving?",
      );
      if (saveNow) {
        const ok = await handleSave();
        if (!ok) return; // stay on tab if save failed
        setActiveTab(nextTabId);
      } else {
        setIsEditing(false); // discard edits
        setEditData(lead); // reset form to saved values
        setActiveTab(nextTabId);
      }
      return;
    }
    setActiveTab(nextTabId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    setEditData(leadData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const {
        id: _id,
        created_at: _ca,
        notes: _notes,
        ...leadFields
      } = editData;
      await leadService.updateLead(leadIdentifier, { lead: leadFields });
      await loadLead();
      setIsEditing(false);
      toast.success("Lead updated successfully");
      onLeadUpdated?.();
      return true;
    } catch (err) {
      toast.error("Failed to save lead");
      console.error(err);
      return false;
    }
  };

  const handleDelete = async () => {
    // if (userPermission !== "masterAccess") {
    //   toast.error("You do not have permission to delete leads");
    //   return;
    // }

    if (!leadData?.id) {
      toast.error("Missing lead identifier");
      return;
    }

    const leadName = leadData?.name || "this lead";
    if (
      window.confirm(
        `Are you sure you want to delete ${leadName}? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${leadName}? Click “Ok” only if you are absolutely sure.`,
      )
    ) {
      try {
        await leadService.deleteLead(leadIdentifier);
        onLeadDeleted?.(leadIdentifier);
        toast.success("Lead deleted successfully");
        navigate("/leads");
      } catch (err) {
        toast.error("Failed to delete lead");
        console.error(err);
      }
    }
  };

  // const serviceOptions = constantValues.serviceOptions.map((s) => ({
  //   value: s.value,
  //   label: s.label,
  // }));

  const leadStatusOptions = constantValues.leadStatusOptions;

  useEffect(() => {
    (async () => {
      try {
        const reps = await clientService.getSalesReps("");
        setSalesRepDefaults(reps.map((r) => ({ value: r.id, label: r.name })));
      } catch (error) {
        console.error("Failed to load sales reps", error);
      }
      try {
        const refs = await leadService.getReferPartners("");
        setReferPartnerDefaults(
          refs.map((r) => ({ value: r.id, label: r.name })),
        );
      } catch (error) {
        console.error("Failed to load refer partners", error);
      }
      try {
        const svcs = await leadService.getServices("");
        setServiceDefaults(svcs.map((r) => ({ value: r.id, label: r.name })));
      } catch (error) {
        console.error("Failed to load services", error);
      }
    })();
  }, []);

  const selectedSalesRepOption = useMemo(() => {
    const repId = editData?.sales_rep_id;
    if (!repId) return null;

    const repName =
      editData?.sales_rep_name ??
      salesRepDefaults.find((option) => String(option.value) === String(repId))
        ?.label ??
      `#${repId}`;

    return { value: repId, label: repName };
  }, [editData?.sales_rep_id, editData?.sales_rep_name, salesRepDefaults]);

  const selectedServiceValues = useMemo(
    () =>
      (editData?.service_ids || []).map((id, i) => {
        const found = serviceDefaults.find(
          (o) => String(o.value) === String(id),
        );
        return (
          found || {
            value: id,
            label: (editData?.service_names || [])[i] || String(id),
          }
        );
      }),
    [editData?.service_ids, editData?.service_names, serviceDefaults],
  );

  const selectedReferPartnerOption = useMemo(() => {
    const refId = editData?.ref_partner_id;
    if (!refId) return null;

    const refName =
      editData?.ref_partner_name ??
      referPartnerDefaults.find(
        (option) => String(option.value) === String(refId),
      )?.label ??
      `#${refId}`;

    return { value: refId, label: refName };
  }, [
    editData?.ref_partner_id,
    editData?.ref_partner_name,
    referPartnerDefaults,
  ]);

  const salesRepOptions = useMemo(() => {
    const selected = selectedSalesRepOption;
    if (!selected) return salesRepDefaults;

    const selectedExists = salesRepDefaults.some(
      (option) => String(option.value) === String(selected.value),
    );
    return selectedExists ? salesRepDefaults : [selected, ...salesRepDefaults];
  }, [selectedSalesRepOption, salesRepDefaults]);

  const referPartnerOptions = useMemo(() => {
    const selected = selectedReferPartnerOption;
    if (!selected) return referPartnerDefaults;

    const selectedExists = referPartnerDefaults.some(
      (option) => String(option.value) === String(selected.value),
    );
    return selectedExists
      ? referPartnerDefaults
      : [selected, ...referPartnerDefaults];
  }, [selectedReferPartnerOption, referPartnerDefaults]);

  const loadSalesRepOptions = useCallback(async (inputValue) => {
    const rows = await clientService.getSalesReps(inputValue);
    return rows.map((row) => ({ value: row.id, label: row.name }));
  }, []);

  const loadReferPartnerOptions = useCallback(async (inputValue) => {
    const rows = await leadService.getReferPartners(inputValue);
    return rows.map((row) => ({ value: row.id, label: row.name }));
  }, []);

  const loadServiceOptions = useCallback(async (inputValue) => {
    const rows = await leadService.getServices(inputValue);
    return rows.map((row) => ({ value: row.id, label: row.name }));
  }, []);

  const debounce = (fn, delay = 300) => {
    let timeout;
    return (...args) =>
      new Promise((resolve) => {
        clearTimeout(timeout);
        timeout = setTimeout(async () => resolve(await fn(...args)), delay);
      });
  };

  const debouncedLoadSales = useMemo(
    () => debounce(loadSalesRepOptions),
    [loadSalesRepOptions],
  );

  const debouncedLoadPartners = useMemo(
    () => debounce(loadReferPartnerOptions),
    [loadReferPartnerOptions],
  );

  const debouncedLoadServices = useMemo(
    () => debounce(loadServiceOptions),
    [loadServiceOptions],
  );

  const handleDeleteServiceOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete service "${option.label}"? This will unassign it from any lead.`,
      )
    ) {
      return;
    }
    try {
      setDeletingServiceId(option.value);
      await leadService.deleteService(option.value);
      setServiceDefaults((prev) =>
        prev.filter((item) => String(item.value) !== String(option.value)),
      );
      setEditData((prev) => ({
        ...prev,
        service_ids: (prev?.service_ids || []).filter(
          (id) => String(id) !== String(option.value),
        ),
        service_names: (prev?.service_names || []).filter(
          (_, i) =>
            String((prev.service_ids || [])[i]) !== String(option.value),
        ),
        services: (prev?.services || []).filter(
          (name) => name !== option.label,
        ),
      }));
      toast.success(`Service "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete service error", error);
      toast.error("Could not delete Service");
    } finally {
      setDeletingServiceId(null);
    }
  }, []);

  const handleDeleteSalesRepOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete sales rep "${option.label}"? This will unassign it from any lead or client.`,
      )
    ) {
      return;
    }

    try {
      setDeletingSalesRepId(option.value);
      await clientService.deleteSalesRep(option.value);
      setSalesRepDefaults((prev) =>
        prev.filter((item) => String(item.value) !== String(option.value)),
      );
      setEditData((prev) => {
        if (String(prev?.sales_rep_id) !== String(option.value)) return prev;
        return { ...prev, sales_rep_id: null, sales_rep_name: "" };
      });
      toast.success(`Sales rep "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete sales rep error", error);
      toast.error("Could not delete Sales Rep");
    } finally {
      setDeletingSalesRepId(null);
    }
  }, []);

  const handleDeleteReferPartnerOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete refer partner "${option.label}"? This will unassign it from any lead or client.`,
      )
    ) {
      return;
    }

    try {
      setDeletingReferPartnerId(option.value);
      await leadService.deleteReferPartner(option.value);
      setReferPartnerDefaults((prev) =>
        prev.filter((item) => String(item.value) !== String(option.value)),
      );
      setEditData((prev) => {
        if (String(prev?.ref_partner_id) !== String(option.value)) return prev;
        return { ...prev, ref_partner_id: null, ref_partner_name: "" };
      });
      toast.success(`Refer partner "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete refer partner error", error);
      toast.error("Could not delete refer partner");
    } finally {
      setDeletingReferPartnerId(null);
    }
  }, []);

  const toggleCredentialPasswordVisibility = (credentialId) => {
    setVisibleCredentialPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(credentialId)) {
        next.delete(credentialId);
      } else {
        next.add(credentialId);
      }
      return next;
    });
  };

  const handleCopyValue = async (value, label) => {
    if (!value) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      toast.error(`Unable to copy ${label}`);
      console.error("Copy clipboard error:", error);
    }
  };

  // QuickBooks handlers
  const startQuickbooksEdit = () => {
    setQuickbooksDraft({
      type: quickbooks?.type || "",
      username: quickbooks?.username || "",
      password: quickbooks?.password || "",
    });
    setIsQuickbooksEditing(true);
  };
  const cancelQuickbooksEdit = () => {
    setIsQuickbooksEditing(false);
    setQuickbooksDraft(createEmptyQuickBooks());
  };
  const handleSaveQuickbooks = async () => {
    if (!quickbooksDraft.type.trim()) {
      toast.error("Type is required");
      return;
    }
    try {
      const updated = await leadService.upsertQuickBooks(
        leadIdentifier,
        quickbooksDraft,
      );
      setQuickbooks(updated);
      toast.success("QuickBooks saved");
      cancelQuickbooksEdit();
    } catch (e) {
      toast.error("Failed to save QuickBooks");
      console.error(e);
    }
  };
  const handleDeleteQuickbooks = async () => {
    if (
      !window.confirm(
        "Delete QuickBooks credentials for this lead? This cannot be undone.",
      )
    )
      return;
    try {
      await leadService.deleteQuickBooks(leadIdentifier);
      setQuickbooks(null);
      cancelQuickbooksEdit();
      toast.success("QuickBooks deleted");
    } catch (e) {
      toast.error("Failed to delete QuickBooks");
      console.error(e);
    }
  };

  // Credential handlers
  const startEditCredential = (cred = null) => {
    if (cred) {
      setEditingCredentialId(cred.id);
      setCredentialDraft({
        id: cred.id,
        entity_name: cred.entity_name || "",
        entity_link: cred.entity_link || "",
        username: cred.username || "",
        password: cred.password || "",
      });
    } else {
      setEditingCredentialId("new");
      setCredentialDraft(createEmptyCredential());
    }
  };
  const cancelCredentialEdit = () => {
    setEditingCredentialId(null);
    setCredentialDraft(createEmptyCredential());
  };
  const handleSaveCredential = async () => {
    if (!credentialDraft.entity_name.trim()) {
      toast.error("Entity name is required");
      return;
    }
    const payload = {
      entity_name: credentialDraft.entity_name.trim(),
      entity_link: credentialDraft.entity_link.trim(),
      username: credentialDraft.username.trim(),
      password: credentialDraft.password,
    };
    try {
      if (!credentialDraft.id) {
        const created = await leadService.createCredential(
          leadIdentifier,
          payload,
        );
        setCredentials((prev) => [...prev, created]);
        toast.success("Credential added");
      } else {
        const updated = await leadService.updateCredential(
          leadIdentifier,
          credentialDraft.id,
          payload,
        );
        setCredentials((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c)),
        );
        toast.success("Credential updated");
      }
      cancelCredentialEdit();
    } catch (e) {
      toast.error("Failed to save credential");
      console.error(e);
    }
  };
  const handleDeleteCredential = async (cred) => {
    if (
      !window.confirm(`Delete credential "${cred.entity_name || "Unnamed"}"?`)
    )
      return;
    try {
      await leadService.deleteCredential(leadIdentifier, cred.id);
      setCredentials((prev) => prev.filter((c) => c.id !== cred.id));
      if (editingCredentialId === cred.id) cancelCredentialEdit();
      toast.success("Credential deleted");
    } catch (e) {
      toast.error("Failed to delete credential");
      console.error(e);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const title = newNoteTitle.trim();
    const content = newNoteContent.trim();
    if (!title && !content) return;

    if (!leadIdentifier) {
      toast.error("Missing lead ID");
      return;
    }

    const optimistic = {
      id: `tmp_${Date.now()}`,
      title,
      content,
      author: currentUser,
      who_updated_last: currentUser,
      asset_url: null,
      _optimistic: true,
    };

    const previousFile = newNoteFile;

    setNotes((prev) => [optimistic, ...prev]);
    setNewNoteTitle("");
    setNewNoteContent("");
    setNewNoteFile(null);
    if (noteFileInputRef.current) {
      noteFileInputRef.current.value = "";
    }

    try {
      let asset_path = undefined;
      if (previousFile) {
        const uploaded = await leadService.uploadNoteAttachment(
          leadIdentifier,
          {
            title: title || "Note",
            author: currentUser,
            file: previousFile,
          },
        );
        // prefer a public URL if your FastAPI returns one (e.g. /files/<name>)
        asset_path = uploaded?.path || null;

        // update optimistic card to show the attachment link ASAP
        if (asset_path) {
          setNotes((prev) =>
            prev.map((n) =>
              n.id === optimistic.id ? { ...n, asset_url: asset_path } : n,
            ),
          );
        }
      }

      const saved = await leadService.addNote(leadIdentifier, {
        title: title || "Note",
        content,
        author: currentUser,
        who_updated_last: currentUser,
        asset_url: asset_path,
      });
      setNotes((prev) =>
        prev.map((n) =>
          n.id === optimistic.id
            ? { ...optimistic, ...saved, _optimistic: false }
            : n,
        ),
      );
      toast.success("Note added successfully");
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n.id !== optimistic.id));
      setNewNoteTitle(title);
      setNewNoteContent(content);
      setNewNoteFile(previousFile || null);
      if (noteFileInputRef.current) noteFileInputRef.current.value = "";
      toast.error("Failed to add note");
      console.error(err);
    }
  };

  const handleNoteFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setNewNoteFile(file);
  };

  const clearNewNoteFile = () => {
    setNewNoteFile(null);
    if (noteFileInputRef.current) {
      noteFileInputRef.current.value = "";
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingTitle(note.title || "");
    setEditingContent(note.content || "");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingTitle("");
    setEditingContent("");
  };

  const saveEditNote = async (id) => {
    const title = editingTitle.trim();
    const content = editingContent.trim();
    const now = new Date().toISOString();

    if (!leadIdentifier) {
      toast.error("Missing lead ID");
      return;
    }

    const prev = notes;
    setNotes((cur) =>
      cur.map((n) =>
        n.id === id
          ? {
              ...n,
              title,
              content,
              who_updated_last: currentUser,
              updated_at: now,
            }
          : n,
      ),
    );
    cancelEditNote();

    try {
      await leadService.updateNote(leadIdentifier, id, {
        title,
        content,
        who_updated_last: currentUser,
      });
      toast.success("Note updated");
    } catch (err) {
      setNotes(prev);
      toast.error("Failed to update note");
      console.error(err);
    }
  };

  const handleDeleteNote = async (event, id) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!leadIdentifier) {
      toast.error("Missing lead ID");
      return;
    }
    const noteTitle =
      notes.find((n) => n.id === id)?.title?.trim() || "this note";
    const confirmed = window.confirm(
      `Delete ${noteTitle}? This cannot be undone.`,
    );
    if (!confirmed) return;
    const prev = notes;
    setNotes((cur) => cur.filter((n) => n.id !== id));

    try {
      await leadService.deleteNote(leadIdentifier, id);
      toast.success("Note deleted");
    } catch (err) {
      setNotes(prev);
      toast.error("Failed to delete note");
      console.error("Delete note error:", err);
    }
  };

  const tabs = useMemo(() => {
    if (!lead) return [];
    return helperService.createLeadTabs({
      notesCount: notes.length,
      credentialsCount: credentials.length,
    });
  }, [lead, notes.length, credentials.length]);

  const infoDirty = useMemo(() => {
    if (!isEditing || !lead) return false;
    const strip = ({ id: _i, created_at: _c, notes: _n, ...rest }) => rest;
    return (
      JSON.stringify(strip(editData || {})) !==
      JSON.stringify(strip(lead || {}))
    );
  }, [isEditing, editData, lead]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="canopy-spinner mx-auto mb-4"></div>
          <p className="text-canopy-textMuted">Loading lead...</p>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Lead not found</p>
      </div>
    );
  }

  const displayFields = [
    { label: "Name", value: leadData.name },
    ...(isPersonalLead
      ? []
      : [{ label: "Contact Name", value: leadData.company_name }]),
    { label: "Type", value: isPersonalLead ? "Personal" : "Business" },
    { label: "EIN", value: helperService.formatEIN(leadData.ein) },
    { label: "Trello Card Name", value: leadData.trello_card },
    ...(isPersonalLead
      ? []
      : [{ label: "Industry", value: leadData.industry }]),
    { label: "Status", value: leadData.status },
    {
      label: "Services",
      value: leadData.service_names?.length
        ? leadData.service_names
        : leadData.services,
    },
    { label: "Did you have an Accountant", value: leadData.accountant },
    { label: "Email", value: leadData.email },
    { label: "Secondary Email", value: leadData.secondary_email },
    { label: "Phone Number", value: leadData.phone },
    { label: "Additional Phone Numbers", value: leadData.additional_phones },
    { label: "Preferred Method of Contact", value: leadData.contact_method },
    { label: "Foreign", value: leadData.international },
    { label: "Referral", value: leadData.referral },
    { label: "Refer Partner", value: leadData.ref_partner_name },
    { label: "Sales Representative", value: leadData.sales_rep_name },
    { label: "Language", value: leadData.language },
  ];

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/leads")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leads
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={`p-3 rounded-lg ${
                isLostLead
                  ? "bg-red-100 text-red-600"
                  : isClosedLead
                    ? "bg-green-100 text-green-600"
                    : isWaitingDocumentsLead
                      ? "bg-yellow-100 text-yellow-600"
                      : isReadyLead
                        ? "bg-purple-100 text-purple-600"
                        : isWaitingAcceptanceLead
                          ? "bg-orange-100 text-orange-600"
                          : "bg-blue-100 text-blue-600"
              }`}
            >
              <Target className="h-6 w-6" />
            </div>
            <div>
              <h1
                className={`text-3xl font-bold ${
                  isLostLead
                    ? "text-red-600"
                    : isClosedLead
                      ? "text-green-600"
                      : isWaitingDocumentsLead
                        ? "text-yellow-600"
                        : isReadyLead
                          ? "text-purple-600"
                          : isWaitingAcceptanceLead
                            ? "text-orange-600"
                            : "text-gray-900"
                }`}
              >
                {leadData.name}
              </h1>
              <p
                className={`mt-1 ${
                  isLostLead
                    ? "text-red-500"
                    : isClosedLead
                      ? "text-green-500"
                      : isWaitingDocumentsLead
                        ? "text-yellow-500"
                        : isReadyLead
                          ? "text-purple-500"
                          : isWaitingAcceptanceLead
                            ? "text-orange-500"
                            : "text-gray-600"
                }`}
              >
                ID {leadData.id}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center px-4 py-2 rounded-lg text-white transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </>
            ) : (
              <>
                {!isLostLead && (
                  <>
                    <button
                      onClick={() =>
                        navigate("/create_client", {
                          state: {
                            leadPrefill: mapLeadToClientPrefill(leadData),
                            convertedFromLeadId: leadIdentifier,
                            leadNotes: notes,
                            leadQuickbooks: quickbooks,
                            leadCredentials: credentials,
                          },
                        })
                      }
                      className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Convert to Client
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-x-6 gap-y-2 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Information Tab */}
          {activeTab === "data" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Lead Information
              </h3>

              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editData.name || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  {!isPersonalLead && (
                    <div>
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={editData.company_name || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      EIN
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="ein"
                      value={editData.ein || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          ein: e.target.value.replace(/\D/g, "").slice(0, 9),
                        }))
                      }
                      maxLength={9}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trello Card Name
                    </label>
                    <input
                      type="text"
                      name="trello_card"
                      value={editData.trello_card || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  {!isPersonalLead && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <input
                        type="text"
                        name="industry"
                        value={editData.industry || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={editData.status || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select status</option>
                      {leadStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services
                    </label>
                    <AsyncCreatableSelect
                      isClearable
                      isMulti
                      cacheOptions
                      defaultOptions={serviceDefaults}
                      loadOptions={debouncedLoadServices}
                      value={selectedServiceValues}
                      components={{ Option: DeletableAsyncOption }}
                      userPermission={userPermission}
                      onDeleteOption={handleDeleteServiceOption}
                      deletingOptionId={deletingServiceId}
                      onChange={(selected) =>
                        setEditData((prev) => ({
                          ...prev,
                          service_ids: (selected || []).map((o) => o.value),
                          service_names: (selected || []).map((o) => o.label),
                          services: (selected || []).map((o) => o.label),
                        }))
                      }
                      onCreateOption={async (name) => {
                        try {
                          const created = await leadService.createService(name);
                          const option = {
                            value: created.id,
                            label: created.name,
                          };
                          setServiceDefaults((prev) => [...prev, option]);
                          setEditData((prev) => ({
                            ...prev,
                            service_ids: [
                              ...(prev.service_ids || []),
                              created.id,
                            ],
                            service_names: [
                              ...(prev.service_names || []),
                              created.name,
                            ],
                            services: [...(prev.services || []), created.name],
                          }));
                        } catch (error) {
                          console.error(error);
                          toast.error("Could not create Service");
                        }
                      }}
                      closeMenuOnSelect={false}
                      placeholder="Select or create services"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Did you have an Accountant?
                    </label>
                    <select
                      name="accountant"
                      value={editData.accountant || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      {constantValues.accountantOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editData.email || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Email
                    </label>
                    <input
                      type="email"
                      name="secondary_email"
                      value={editData.secondary_email || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={editData.phone || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Phone Numbers
                    </label>
                    <input
                      type="tel"
                      name="additional_phones"
                      value={editData.additional_phones || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Method of Contact
                    </label>
                    <select
                      name="contact_method"
                      value={editData.contact_method || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select method</option>
                      {constantValues.contactMethodOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foreign
                    </label>
                    <select
                      name="international"
                      value={editData.international || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      {constantValues.internationalOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referral
                    </label>
                    <select
                      name="referral"
                      value={editData.referral || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      {constantValues.referralOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Refer Partner
                    </label>
                    <AsyncCreatableSelect
                      isClearable
                      cacheOptions
                      defaultOptions={referPartnerOptions}
                      loadOptions={debouncedLoadPartners}
                      value={selectedReferPartnerOption}
                      components={{ Option: DeletableAsyncOption }}
                      userPermission={userPermission}
                      onDeleteOption={handleDeleteReferPartnerOption}
                      deletingOptionId={deletingReferPartnerId}
                      onChange={(option) =>
                        setEditData((prev) => ({
                          ...prev,
                          ref_partner_id: option?.value ?? null,
                          ref_partner_name: option?.label ?? "",
                        }))
                      }
                      onCreateOption={async (name) => {
                        try {
                          const created =
                            await leadService.createReferPartner(name);
                          const option = {
                            value: created.id,
                            label: created.name,
                          };
                          setReferPartnerDefaults((prev) => [...prev, option]);
                          setEditData((prev) => ({
                            ...prev,
                            ref_partner_id: created.id,
                            ref_partner_name: created.name,
                          }));
                        } catch (error) {
                          console.error(error);
                          toast.error("Could not create refer partner");
                        }
                      }}
                      placeholder="Select or create a refer partner"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">
                      Sales Representative *
                    </label>
                    <AsyncCreatableSelect
                      isClearable
                      cacheOptions
                      defaultOptions={salesRepOptions}
                      loadOptions={debouncedLoadSales}
                      value={selectedSalesRepOption}
                      components={{ Option: DeletableAsyncOption }}
                      userPermission={userPermission}
                      onDeleteOption={handleDeleteSalesRepOption}
                      deletingOptionId={deletingSalesRepId}
                      onChange={(option) =>
                        setEditData((prev) => ({
                          ...prev,
                          sales_rep_id: option?.value ?? null,
                          sales_rep_name: option?.label ?? "",
                        }))
                      }
                      onCreateOption={async (name) => {
                        try {
                          const created =
                            await clientService.createSalesRep(name);
                          const option = {
                            value: created.id,
                            label: created.name,
                          };
                          setSalesRepDefaults((prev) => [...prev, option]);
                          setEditData((prev) => ({
                            ...prev,
                            sales_rep_id: created.id,
                            sales_rep_name: created.name,
                          }));
                        } catch (error) {
                          console.error(error);
                          toast.error("Could not create Sales Rep");
                        }
                      }}
                      placeholder="Select or create a sales rep"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      name="language"
                      value={editData.language || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select language</option>
                      {constantValues.languageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {displayFields.map((field) => (
                    <div key={field.label}>
                      <dt className="text-sm font-medium text-gray-500">
                        {field.label}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {helperService.formatValue(field.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Documents
                </h3>

                {isEditing && userPermission === "masterAccess" ? (
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <input
                      type="text"
                      value={editData.documents_url || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          documents_url: {
                            ...(prev.documents_url || {}),
                            url: e.target.value,
                          },
                        }))
                      }
                      className="w-full md:w-[550px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                ) : lead.documents_url ? (
                  <a
                    href={lead.documents_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> Open SharePoint
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">
                    No SharePoint link available
                  </span>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-1">
                  SharePoint Integration
                </h4>
                <p className="text-gray-600">
                  All lead documents are managed through SharePoint.{" "}
                  {lead.documents_url
                    ? "Use the button above to access the folder."
                    : "Add the SharePoint URL above to enable quick access."}
                </p>
              </div>
            </div>
          )}

          {/* QuickBooks Tab */}
          {activeTab === "quickbooks" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  QuickBooks Information
                </h3>
                <div className="flex items-center gap-2">
                  {quickbooks ? (
                    isQuickbooksEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={cancelQuickbooksEdit}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveQuickbooks}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                        >
                          Save QuickBooks
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={startQuickbooksEdit}
                          className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteQuickbooks}
                          className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )
                  ) : isQuickbooksEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={cancelQuickbooksEdit}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveQuickbooks}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                      >
                        Save QuickBooks
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={startQuickbooksEdit}
                      className="flex items-center px-4 py-2 border border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add QuickBooks
                    </button>
                  )}
                </div>
              </div>

              {isQuickbooksEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={quickbooksDraft.type}
                      onChange={(e) =>
                        setQuickbooksDraft((p) => ({
                          ...p,
                          type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select type</option>
                      {constantValues.quickbooksTypeOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={quickbooksDraft.username}
                      onChange={(e) =>
                        setQuickbooksDraft((p) => ({
                          ...p,
                          username: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={quickbooksDraft.password}
                        onChange={(e) =>
                          setQuickbooksDraft((p) => ({
                            ...p,
                            password: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOffIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : quickbooks ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(quickbooks).map(([key, value]) => {
                    if (!value || key === "id" || key === "lead_id")
                      return null;
                    const label = key.charAt(0).toUpperCase() + key.slice(1);
                    const isPassword = key === "password";
                    const displayValue = isPassword
                      ? showPassword
                        ? value
                        : "••••••••"
                      : value;
                    return (
                      <div key={key}>
                        <dt className="text-sm font-medium text-gray-500">
                          {label}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                          {displayValue}
                          {isPassword && (
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? (
                                <EyeOffIcon className="w-5 h-5" />
                              ) : (
                                <EyeIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {(key === "username" || isPassword) && (
                            <button
                              type="button"
                              onClick={() => handleCopyValue(value, label)}
                              className="text-gray-400 hover:text-primary-600"
                              aria-label={`Copy ${label}`}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No QuickBooks data available
                </p>
              )}
            </div>
          )}

          {/* Credentials Tab */}
          {activeTab === "credentials" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Credentials & Access
                </h3>
                <button
                  type="button"
                  onClick={() => startEditCredential()}
                  disabled={Boolean(editingCredentialId)}
                  className={`flex items-center px-4 py-2 rounded-lg border ${editingCredentialId ? "border-gray-200 text-gray-400 cursor-not-allowed" : "border-dashed border-gray-300 text-gray-700 hover:bg-gray-50"} transition-colors`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Credential
                </button>
              </div>

              {editingCredentialId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {credentialDraft.id ? "Edit Credential" : "New Credential"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entity Name
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.entity_name}
                        onChange={(e) =>
                          setCredentialDraft((p) => ({
                            ...p,
                            entity_name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entity Link
                      </label>
                      <input
                        type="url"
                        value={credentialDraft.entity_link}
                        onChange={(e) =>
                          setCredentialDraft((p) => ({
                            ...p,
                            entity_link: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.username}
                        onChange={(e) =>
                          setCredentialDraft((p) => ({
                            ...p,
                            username: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.password}
                        onChange={(e) =>
                          setCredentialDraft((p) => ({
                            ...p,
                            password: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelCredentialEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveCredential}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Save Credential
                    </button>
                  </div>
                </div>
              )}

              {credentials.length > 0 ? (
                <div className="space-y-4">
                  {[...credentials]
                    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
                    .map((cred) => {
                      if (editingCredentialId === cred.id) return null;
                      const label =
                        cred.entity_name?.trim() || "Unnamed entity";
                      return (
                        <div
                          key={cred.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                {label}
                              </h4>
                              {cred.entity_link ? (
                                <a
                                  href={cred.entity_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary-600 hover:underline break-words"
                                >
                                  {cred.entity_link}
                                </a>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No link provided
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => startEditCredential(cred)}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCredential(cred)}
                                className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Username
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                                {cred.username || (
                                  <span className="text-gray-400 italic">
                                    Not provided
                                  </span>
                                )}
                                {cred.username && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleCopyValue(
                                        cred.username,
                                        `${label} username`,
                                      )
                                    }
                                    className="text-gray-400 hover:text-primary-600"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Password
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                                {cred.password ? (
                                  visibleCredentialPasswords.has(cred.id) ? (
                                    cred.password
                                  ) : (
                                    "••••••••"
                                  )
                                ) : (
                                  <span className="text-gray-400 italic">
                                    Not provided
                                  </span>
                                )}
                                {cred.password && (
                                  <>
                                    <button
                                      type="button"
                                      aria-label={
                                        visibleCredentialPasswords.has(cred.id)
                                          ? "Hide password"
                                          : "Show password"
                                      }
                                      onClick={() =>
                                        toggleCredentialPasswordVisibility(
                                          cred.id,
                                        )
                                      }
                                      className="text-gray-400 hover:text-gray-700"
                                    >
                                      {visibleCredentialPasswords.has(
                                        cred.id,
                                      ) ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                      ) : (
                                        <EyeIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleCopyValue(
                                          cred.password,
                                          `${label} password`,
                                        )
                                      }
                                      className="text-gray-400 hover:text-primary-600"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-gray-500 italic">No credentials stored</p>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === "notes" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              </div>

              {/* Add Note */}
              <form
                onSubmit={handleAddNote}
                className="bg-gray-50 rounded-lg p-4"
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Note
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newNoteTitle}
                    onChange={(e) => setNewNoteTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex gap-3">
                    <textarea
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      placeholder="Write your note…"
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => noteFileInputRef.current?.click()}
                        className="flex items-center justify-center px-4 py-2 border border-dashed border-gray-300 text-gray-600 hover:text-primary-600 hover:border-primary-500 rounded-lg transition-colors"
                      >
                        <Paperclip className="h-4 w-4 mr-2" /> Upload Attachment
                      </button>
                      <button
                        type="submit"
                        className="flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add
                      </button>
                    </div>
                  </div>
                  <input
                    ref={noteFileInputRef}
                    type="file"
                    onChange={handleNoteFileChange}
                    className="hidden"
                  />
                  {newNoteFile && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Paperclip className="h-4 w-4" />
                      <span className="truncate max-w-xs">
                        {newNoteFile.name}
                      </span>
                      {typeof newNoteFile.size === "number" && (
                        <span className="text-gray-400">
                          • {helperService.formatBytes(newNoteFile.size)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={clearNewNoteFile}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </form>

              {/* Notes List */}
              <div className="space-y-4">
                {notes.length > 0 ? (
                  notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">
                            <span className="font-medium text-gray-900">
                              {note.author || "You"}
                            </span>
                            {" • "}
                            <span className="text-xs text-gray-500">
                              Last updated by{" "}
                              <span className="font-medium">
                                {note.who_updated_last || note.author}
                              </span>
                            </span>
                          </span>
                          {note._optimistic && (
                            <span className="text-xs text-amber-600 mt-0.5">
                              Saving…
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {editingNoteId === note.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEditNote(note.id)}
                                className="px-2 py-1 text-xs rounded bg-primary-600 text-white hover:bg-primary-700"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditNote}
                                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => startEditNote(note)}
                                className="text-gray-400 hover:text-gray-700"
                                aria-label="Edit note"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M3 21h3.75L20.5 7.25a1.75 1.75 0 00-2.475-2.475L4.275 18.525 3 21z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={(event) =>
                                  handleDeleteNote(event, note.id)
                                }
                                className="text-gray-400 hover:text-rose-600"
                                aria-label="Delete note"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            placeholder="Title"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <textarea
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      ) : (
                        <>
                          {note.title && (
                            <h4 className="text-base font-semibold text-gray-900">
                              {note.title}
                            </h4>
                          )}
                          {note.content && (
                            <p className="text-gray-700 whitespace-pre-wrap mt-1">
                              {note.content}
                            </p>
                          )}
                          {note.asset_url && (
                            <div className="mt-3 space-y-2">
                              {/* Path + copy */}
                              <div className="inline-flex items-center gap-2 text-sm text-gray-700 break-all">
                                <Paperclip className="h-4 w-4 text-gray-500" />
                                <span className="font-mono">
                                  {note.asset_url}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCopyValue(note.asset_url, "File path")
                                  }
                                  className="text-gray-400 hover:text-primary-600"
                                  aria-label="Copy file path"
                                  title="Copy file path"
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Actions */}
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => loadPreview(note)}
                                  className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                                >
                                  Preview
                                </button>
                              </div>

                              {/* Preview area */}
                              {previews[note.id]?.url && (
                                <div className="mt-2 border rounded-lg overflow-hidden">
                                  {/* Basic type routing */}
                                  {previews[note.id].type.startsWith(
                                    "image/",
                                  ) && (
                                    <img
                                      src={previews[note.id].url}
                                      alt="Attachment preview"
                                      className="max-h-96 w-auto object-contain mx-auto"
                                    />
                                  )}

                                  {previews[note.id].type ===
                                    "application/pdf" && (
                                    <iframe
                                      title="PDF preview"
                                      src={previews[note.id].url}
                                      className="w-full h-96"
                                    />
                                  )}

                                  {previews[note.id].type.startsWith(
                                    "text/",
                                  ) && (
                                    <pre className="p-3 text-sm overflow-auto max-h-96 bg-gray-50">
                                      {previews[note.id].text ?? "—"}
                                    </pre>
                                  )}

                                  {/* Fallback for other binary formats */}
                                  {!previews[note.id].type.startsWith(
                                    "image/",
                                  ) &&
                                    previews[note.id].type !==
                                      "application/pdf" &&
                                    previews[note.id].type !==
                                      "application/json" &&
                                    !previews[note.id].type.startsWith(
                                      "text/",
                                    ) && (
                                      <div className="p-3 text-sm text-gray-600">
                                        Preview not supported (
                                        {previews[note.id].type}).{" "}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            // trigger a temporary download from the blob url
                                            const a =
                                              document.createElement("a");
                                            a.href = previews[note.id].url;
                                            a.download =
                                              note.title || "attachment";
                                            a.click();
                                          }}
                                          className="text-primary-600 hover:underline"
                                        >
                                          Download file
                                        </button>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">
                      No notes yet. Add your first note above.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
